import React, {useEffect, useMemo, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {Link} from 'react-router-dom'
import {toast} from 'react-toastify'
import {useAuth} from '../../auth'
import {getAssignedTemplatesByVesselId, getCreatedReportById} from '../core/_requests'
import {Voyage} from '../core/_models'
import {resolveReportAssignmentScope} from '../core/reportAssignmentScope'
import {ListButtonSkeleton} from '../../../shared/components/LoadingSkeletons'

interface Props {
  isOpen: boolean
  onClose: () => void
  selectedVoyage: Voyage | null
}

const CreateReportModal: React.FC<Props> = ({onClose, isOpen, selectedVoyage}) => {
  const [reports, setReports] = useState<{assignmentId: number; reportName: string}[]>([])
  const [loading, setLoading] = useState(false)
  const [scopeError, setScopeError] = useState<string | null>(null)
  const {currentUser} = useAuth()

  const assignmentScope = useMemo(
    () => resolveReportAssignmentScope({currentUser, selectedVoyage}),
    [currentUser, selectedVoyage]
  )

  useEffect(() => {
    if (!isOpen) return

    const fetchAssignedTemplates = async () => {
      if (!assignmentScope) {
        setReports([])
        setScopeError(
          selectedVoyage
            ? 'Unable to resolve assignment scope for the selected voyage.'
            : 'Select a voyage to load assigned reports.'
        )
        return
      }

      try {
        setLoading(true)
        setScopeError(null)

        if (selectedVoyage?.id) {
          const freshReports = await getCreatedReportById(assignmentScope.vesselId || undefined)
          const targetVoyageId = Number(selectedVoyage.id)
          const activeDraft = Array.isArray(freshReports)
            ? freshReports.find((r: any) => {
                if (String(r.status ?? '').trim().toUpperCase() !== 'DRAFT') return false
                const rVoyId = Number(
                  r?.voyage?.id ?? r?.voyageDto?.id ?? r?.voyageId ?? r?.voyage ?? 0
                )
                return rVoyId === targetVoyageId
              })
            : null

          if (activeDraft) {
            toast.error(
              `An active draft report already exists for this voyage (${activeDraft.reportType || 'Draft Report'}). You cannot create a new report until the existing draft is submitted or deleted.`,
              {position: 'top-center'}
            )
            onClose()
            return
          }
        }

        const filteredList = await getAssignedTemplatesByVesselId(
          assignmentScope.companyGroupAdminId,
          assignmentScope.companyAdminId,
          assignmentScope.vesselType,
          assignmentScope.vesselId
        )

        if (!Array.isArray(filteredList)) {
          setReports([])
          setScopeError('Invalid template list response.')
          return
        }

        const result = filteredList.flatMap((item) => {
          const menus = item.customTemplate?.menus || item.template?.menus || []
          return menus
            .filter((menu: {isActive: boolean}) => menu.isActive)
            .map((menu: {name: string | null | undefined}) => ({
              assignmentId: item.id,
              reportName: menu.name ?? 'Unnamed Report',
            }))
        })

        setReports(result)
      } catch (error) {
        console.error('Failed to fetch template list:', error)
        setReports([])
        setScopeError('Failed to load assigned reports for the selected voyage.')
      } finally {
        setLoading(false)
      }
    }

    void fetchAssignedTemplates()
  }, [assignmentScope, isOpen, selectedVoyage])

  if (!isOpen) return null

  return (
    <div className='custom-modal-overlay' onClick={onClose}>
      <div className='custom-modal' onClick={(e) => e.stopPropagation()}>
        <div className='custom-modal-header' style={{backgroundColor: '#F4F9FF'}}>
          <div className='p-2 d-flex justify-content-between align-items-center w-100'>
            <div>
              <h5 className='mb-1'>Choose next possible reports</h5>
              {selectedVoyage && (
                <div className='text-muted fs-7'>
                  <span className='fw-semibold text-gray-800'>{selectedVoyage.voyageNumber}</span>
                  {selectedVoyage.legs && selectedVoyage.legs.length > 0 && (() => {
                    const activeLeg = selectedVoyage.legs.find((l) => l.active) || selectedVoyage.legs[0]
                    return (
                      <span className='ms-2 badge badge-light-info text-info fw-semibold' style={{fontSize: '11px'}}>
                        L{activeLeg.sequenceNumber}: {activeLeg.departurePort} → {activeLeg.arrivalPort || 'TBD'}
                      </span>
                    )
                  })()}
                </div>
              )}
            </div>
            <button onClick={onClose} className='close-btn'>
              &times;
            </button>
          </div>
        </div>

        <div
          className='custom-modal-body'
          style={{
            maxHeight: '100vh',
            overflowY: 'auto',
            paddingRight: '1rem',
          }}
        >
          <div style={{paddingRight: '5rem', marginBottom: '2rem'}}>
            <p
              className='pb-2 ps-3'
              style={{color: '#1C64F2', borderBottom: '1px solid #1C64F2'}}
            >
              Assigned reports
            </p>
          </div>

          {loading && (
            <div className='ps-3'>
              <ListButtonSkeleton count={5} />
            </div>
          )}

          {!loading && scopeError && <p className='text-muted ps-3'>{scopeError}</p>}

          {!loading && !scopeError && reports.length === 0 && (
            <p className='text-muted ps-3'>No reports are assigned for this vessel.</p>
          )}

          {!loading && !scopeError && reports.map((report, index) => {
            const activeLeg = selectedVoyage?.legs?.find((l) => l.active) || selectedVoyage?.legs?.[0]
            return (
              <div key={index} className='mb-5' style={{paddingRight: '6rem'}}>
                <p className='mb-1'>{report.reportName}</p>
                <Link
                  to={`/operations/create/${report.assignmentId}`}
                  state={{
                    selectedVoyage,
                    selectedTabId: 0,
                    selectedReportName: report.reportName,
                    selectedLegId: activeLeg?.id,
                    selectedLeg: activeLeg,
                  }}
                  className='ms-0 ps-3 btn border d-flex align-items-center gap-1'
                >
                  <KTSVG path='media/map/Files.svg' className='svg-icon-2' />
                  {report.reportName}
                </Link>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default CreateReportModal
